import React, { useState, useEffect } from 'react';
import { X, Building2, User, Calendar, CreditCard, Download, ArrowRight } from 'lucide-react';
import { jsPDF } from 'jspdf';
import toast from 'react-hot-toast';
import { formatCurrency, calculateDailyPayment } from '../../utils/currency';
import { MOTORCYCLE_MODELS } from '../../constants/products';
import EmailFormHandler from './EmailFormHandler';

interface SimulationFormProps {
  isOpen: boolean;
  onClose: () => void;
}

const PROMO_CODES = {
  'EARLY2024': 0.1, // 10% discount
  'WELCOME': 0.05 // 5% discount
};

const MIN_DOWNPAYMENT = 150000; // 150,000 FCFA minimum
const PAYMENT_PERIODS = [6, 12, 18, 24];

interface SimulationResult {
  model: string;
  basePrice: number;
  downPayment: number;
  monthlyPayment: number;
  dailyPayment: number;
  duration: number;
  totalAmount: number;
  firstMonthFree: boolean;
  fleetSize?: number;
  discount?: number;
}

const SimulationForm: React.FC<SimulationFormProps> = ({ isOpen, onClose }) => {
  const [accountType, setAccountType] = useState<'individual' | 'business'>('individual');
  const [formData, setFormData] = useState({
    model: '',
    downPayment: MIN_DOWNPAYMENT,
    duration: 12,
    promoCode: '',
    fleetSize: 1,
    // Personal info
    fullName: '',
    neighborhood: '',
    licenseNumber: '',
    phone: '',
    email: '',
    deliveryDate: '',
  });
  const [simulation, setSimulation] = useState<SimulationResult | null>(null);
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (formData.model && formData.downPayment >= MIN_DOWNPAYMENT) {
      calculateSimulation();
    }
  }, [formData.model, formData.downPayment, formData.duration, formData.fleetSize, appliedDiscount]);

  const calculateSimulation = () => {
    const selectedModel = MOTORCYCLE_MODELS.find(m => m.id === formData.model);
    if (!selectedModel) return;

    let basePrice = selectedModel.basePrice;
    let discount = 0;

    // Apply fleet discount for business users
    if (accountType === 'business' && formData.fleetSize >= 5) {
      discount = 0.1; // 10% discount for fleet
    }

    // Apply promo code discount
    if (appliedDiscount > 0) {
      discount = Math.max(discount, appliedDiscount);
    }

    const totalAmount = basePrice * (1 - discount);
    const monthlyPayment = (totalAmount - formData.downPayment) / formData.duration;
    const dailyPayment = calculateDailyPayment(totalAmount, formData.downPayment, formData.duration);

    setSimulation({
      model: selectedModel.name,
      basePrice: basePrice,
      downPayment: formData.downPayment,
      monthlyPayment,
      dailyPayment,
      duration: formData.duration,
      totalAmount,
      firstMonthFree: appliedDiscount > 0,
      fleetSize: accountType === 'business' ? formData.fleetSize : undefined,
      discount: discount > 0 ? discount * 100 : undefined
    });
  };

  const handlePromoCode = () => {
    const discount = PROMO_CODES[formData.promoCode as keyof typeof PROMO_CODES];
    if (discount) {
      setAppliedDiscount(discount);
      toast.success(`Code promo appliqué ! -${discount * 100}% et premier mois gratuit !`);
    } else {
      toast.error('Code promo invalide');
    }
  };

  const handleDownloadPDF = () => {
    if (!simulation) return;

    const doc = new jsPDF();
    
    // Add header
    doc.setFontSize(20);
    doc.text('Simulation Pay As You Go', 20, 20);
    
    // Add simulation details
    doc.setFontSize(12);
    doc.text(`Modèle: ${simulation.model}`, 20, 40);
    doc.text(`Prix de base: ${formatCurrency(simulation.basePrice)}`, 20, 50);
    doc.text(`Acompte: ${formatCurrency(simulation.downPayment)}`, 20, 60);
    doc.text(`Durée: ${simulation.duration} mois`, 20, 70);
    doc.text(`Paiement mensuel: ${formatCurrency(Math.round(simulation.monthlyPayment))}`, 20, 80);
    doc.text(`Paiement journalier: ${formatCurrency(simulation.dailyPayment)}`, 20, 90);
    
    if (simulation.firstMonthFree) {
      doc.text('Premier mois gratuit !', 20, 100);
    }
    
    if (simulation.discount) {
      doc.text(`Remise appliquée: ${simulation.discount}%`, 20, 110);
    }
    
    doc.text(`Montant total: ${formatCurrency(simulation.totalAmount)}`, 20, 120);
    
    // Save the PDF
    doc.save('simulation-payg.pdf');
    toast.success('Simulation téléchargée en PDF');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setSubmitting(true);
    // The actual form submission is handled by EmailFormHandler
    // This is just for UI feedback
    setTimeout(() => {
      handleDownloadPDF();
      onClose();
      setSubmitting(false);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-2xl font-bold text-neutral-900">Simuler votre Plan Pay As You Go</h3>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {/* Account Type Selection */}
          <div className="flex gap-4 mb-8">
            <button
              onClick={() => setAccountType('individual')}
              className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                accountType === 'individual'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-200'
              }`}
            >
              <User size={24} className={`mx-auto mb-2 ${accountType === 'individual' ? 'text-primary-500' : 'text-gray-400'}`} />
              <div className={`font-medium ${accountType === 'individual' ? 'text-primary-500' : 'text-gray-600'}`}>
                Particulier
              </div>
            </button>
            <button
              onClick={() => setAccountType('business')}
              className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                accountType === 'business'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-200'
              }`}
            >
              <Building2 size={24} className={`mx-auto mb-2 ${accountType === 'business' ? 'text-primary-500' : 'text-gray-400'}`} />
              <div className={`font-medium ${accountType === 'business' ? 'text-primary-500' : 'text-gray-600'}`}>
                Entreprise
              </div>
            </button>
          </div>

          {accountType === 'business' && (
            <div className="mb-6 p-4 bg-primary-50 border border-primary-100 rounded-lg">
              <p className="text-primary-700">
                <span className="font-medium">Offre Spéciale Entreprises :</span> Bénéficiez d'une remise de 10% à partir de 5 motos commandées.
              </p>
            </div>
          )}

          <EmailFormHandler formName="Simulation Pay As You Go" className="space-y-6" onSubmit={handleSubmit}>
            {/* Hidden fields for email template */}
            <input type="hidden" name="account_type" value={accountType} />
            {simulation && (
              <>
                <input type="hidden" name="model" value={simulation.model} />
                <input type="hidden" name="base_price" value={simulation.basePrice.toString()} />
                <input type="hidden" name="down_payment" value={simulation.downPayment.toString()} />
                <input type="hidden" name="monthly_payment" value={Math.round(simulation.monthlyPayment).toString()} />
                <input type="hidden" name="daily_payment" value={simulation.dailyPayment.toString()} />
                <input type="hidden" name="duration" value={simulation.duration.toString()} />
                <input type="hidden" name="total_amount" value={simulation.totalAmount.toString()} />
                <input type="hidden" name="first_month_free" value={simulation.firstMonthFree ? 'Oui' : 'Non'} />
                {simulation.discount && (
                  <input type="hidden" name="discount" value={`${simulation.discount}%`} />
                )}
              </>
            )}
            
            {/* Model Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Modèle *
              </label>
              <select
                name="model"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              >
                <option value="">Sélectionnez un modèle</option>
                {MOTORCYCLE_MODELS.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.name} - {formatCurrency(model.basePrice)}
                  </option>
                ))}
              </select>
            </div>

            {/* Fleet Size for Business */}
            {accountType === 'business' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de motos *
                </label>
                <input
                  type="number"
                  min="1"
                  name="fleet_size"
                  value={formData.fleetSize}
                  onChange={(e) => setFormData({ ...formData, fleetSize: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
                {formData.fleetSize < 5 && (
                  <p className="mt-2 text-sm text-gray-500">
                    Commandez 5 motos ou plus pour bénéficier de la remise entreprise.
                  </p>
                )}
              </div>
            )}

            {/* Down Payment Slider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Acompte Initial *
              </label>
              <div className="space-y-4">
                <input
                  type="range"
                  min={MIN_DOWNPAYMENT}
                  max={formData.model ? MOTORCYCLE_MODELS.find(m => m.id === formData.model)?.basePrice || MIN_DOWNPAYMENT : MIN_DOWNPAYMENT}
                  step={10000}
                  value={formData.downPayment}
                  onChange={(e) => setFormData({ ...formData, downPayment: parseInt(e.target.value) })}
                  className="w-full accent-primary-500"
                />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Minimum: {formatCurrency(MIN_DOWNPAYMENT)}</span>
                  <span className="text-lg font-medium text-primary-600">{formatCurrency(formData.downPayment)}</span>
                </div>
              </div>
              <input type="hidden" name="down_payment" value={formData.downPayment.toString()} />
            </div>

            {/* Duration Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Durée de financement *
              </label>
              <div className="grid grid-cols-4 gap-3">
                {PAYMENT_PERIODS.map(period => (
                  <button
                    key={period}
                    type="button"
                    onClick={() => setFormData({ ...formData, duration: period })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.duration === period
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-primary-200 text-gray-600'
                    }`}
                  >
                    {period} mois
                  </button>
                ))}
              </div>
              <input type="hidden" name="duration" value={formData.duration.toString()} />
            </div>

            {/* Promo Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Code Promo
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="promo_code"
                  value={formData.promoCode}
                  onChange={(e) => setFormData({ ...formData, promoCode: e.target.value.toUpperCase() })}
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Entrez votre code"
                />
                <button
                  type="button"
                  onClick={handlePromoCode}
                  className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Appliquer
                </button>
              </div>
            </div>

            {/* Simulation Results */}
            {simulation && (
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">Récapitulatif</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Modèle</p>
                    <p className="text-lg font-medium">{simulation.model}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Prix de base</p>
                    <p className="text-lg font-medium">{formatCurrency(simulation.basePrice)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Acompte</p>
                    <p className="text-lg font-medium">{formatCurrency(simulation.downPayment)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Durée</p>
                    <p className="text-lg font-medium">{simulation.duration} mois</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Paiement mensuel</p>
                    <p className="text-lg font-medium">{formatCurrency(Math.round(simulation.monthlyPayment))}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Paiement journalier</p>
                    <p className="text-lg font-medium">{formatCurrency(simulation.dailyPayment)}</p>
                  </div>
                </div>

                {(simulation.firstMonthFree || simulation.discount) && (
                  <div className="bg-green-50 p-4 rounded-lg mt-4">
                    {simulation.firstMonthFree && (
                      <p className="text-green-700 font-medium">🎁 Premier mois gratuit !</p>
                    )}
                    {simulation.discount && (
                      <p className="text-green-700 font-medium">💰 Remise de {simulation.discount}% appliquée</p>
                    )}
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Coût total</p>
                      <p className="text-2xl font-bold text-primary-600">{formatCurrency(simulation.totalAmount)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleDownloadPDF}
                      className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Download size={20} className="mr-2" />
                      Télécharger PDF
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Personal Information */}
            {simulation && (
              <>
                <h4 className="text-lg font-semibold text-gray-900 pt-4">Vos Informations</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quartier de résidence *
                    </label>
                    <input
                      type="text"
                      name="neighborhood"
                      value={formData.neighborhood}
                      onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Numéro de permis *
                    </label>
                    <input
                      type="text"
                      name="license_number"
                      value={formData.licenseNumber}
                      onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date de livraison souhaitée *
                    </label>
                    <div className="relative">
                      <Calendar size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="date"
                        name="delivery_date"
                        value={formData.deliveryDate}
                        onChange={(e) => setFormData({ ...formData, deliveryDate: e.target.value })}
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end space-x-4 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={!simulation || submitting}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 inline-flex items-center"
              >
                {submitting ? 'Envoi en cours...' : (
                  <>
                    Envoyer la demande
                    <ArrowRight size={20} className="ml-2" />
                  </>
                )}
              </button>
            </div>
          </EmailFormHandler>
        </div>
      </div>
    </div>
  );
};

export default SimulationForm;